import React, { useEffect, useRef, useState } from "react";

/* RepIQ's own confirm / prompt / alert (Salem 2026-10-12: the browser's "repiq.co.uk says" box
   was off-theme and pinned to the top of the screen).

   Promise-based so a call site reads the way window.confirm did:
       if (!(await confirmDialog("Delete this?"))) return;
       const name = await promptDialog("Name the board", "");     // string, or null on cancel
       alertDialog("Couldn't run the test");                       // fire and forget

   <DialogHost /> is mounted once in App; anything asked before it mounts waits in a queue. */

let host = null;                 // the mounted DialogHost's `open` function
const queue = [];                // asked before the host mounted

const open = (spec) => new Promise((resolve) => {
  const item = { ...spec, resolve };
  if (host) host(item); else queue.push(item);
});

const DANGER = /\b(delete|remove|erase|forget|permanently|clear|disconnect|sign out)\b/i;
const verb = (m) => {
  const w = String(m || "").match(DANGER);
  if (!w) return null;
  const v = w[1].toLowerCase();
  return v === "permanently" ? "Delete" : v.charAt(0).toUpperCase() + v.slice(1);
};

export function confirmDialog(message, opts = {}) {
  const danger = opts.danger ?? DANGER.test(String(message || ""));
  return open({ kind: "confirm", message, title: opts.title || "Are you sure?", danger,
    confirmLabel: opts.confirmLabel || (danger ? (verb(message) || "Confirm") : "Confirm"),
    cancelLabel: opts.cancelLabel || "Cancel" });
}

export function promptDialog(message, defaultValue = "", opts = {}) {
  return open({ kind: "prompt", message, value: defaultValue ?? "", title: opts.title || "RepIQ",
    confirmLabel: opts.confirmLabel || "OK", cancelLabel: "Cancel", placeholder: opts.placeholder || "" });
}

export function alertDialog(message, opts = {}) {
  return open({ kind: "alert", message, title: opts.title || "RepIQ", confirmLabel: "OK" });
}

export function DialogHost() {
  const [stack, setStack] = useState([]);
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  const okRef = useRef(null);
  const cur = stack[0] || null;

  useEffect(() => {
    host = (item) => setStack((s) => [...s, item]);
    queue.splice(0).forEach((q) => host(q));
    return () => { host = null; };
  }, []);

  // each new dialog starts with its own default text and focus
  useEffect(() => {
    if (!cur) return;
    setText(cur.kind === "prompt" ? String(cur.value ?? "") : "");
    const t = setTimeout(() => {
      if (cur.kind === "prompt") { inputRef.current?.focus(); inputRef.current?.select?.(); }
      else okRef.current?.focus();
    }, 20);
    return () => clearTimeout(t);
  }, [cur]);

  const close = (result) => {
    if (!cur) return;
    cur.resolve(result);
    setStack((s) => s.slice(1));
  };
  const cancelValue = cur?.kind === "prompt" ? null : cur?.kind === "alert" ? true : false;
  const accept = () => close(cur.kind === "prompt" ? text : true);

  useEffect(() => {
    if (!cur) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(cancelValue); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cur) return null;
  const lines = String(cur.message ?? "");
  return (
    <div className="dlg-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) close(cancelValue); }}>
      <div className={"dlg" + (cur.danger ? " dlg-danger" : "")} role={cur.kind === "alert" ? "alertdialog" : "dialog"}
        aria-modal="true" aria-label={cur.title}
        onKeyDown={(e) => { if (e.key === "Enter" && cur.kind !== "prompt" && e.target.tagName !== "BUTTON") accept(); }}>
        <div className="dlg-head">
          <span className="dlg-mark" aria-hidden>{cur.danger ? "!" : cur.kind === "prompt" ? "✎" : "?"}</span>
          <h3 className="dlg-title">{cur.kind === "prompt" || cur.kind === "alert" ? cur.title : cur.title}</h3>
        </div>
        <div className="dlg-body">{lines}</div>
        {cur.kind === "prompt" && (
          <input ref={inputRef} className="input dlg-input" value={text} placeholder={cur.placeholder}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); accept(); } }} />
        )}
        <div className="dlg-actions">
          {cur.kind !== "alert" && (
            <button type="button" className="btn btn-outline" onClick={() => close(cancelValue)}>{cur.cancelLabel}</button>
          )}
          <button type="button" ref={okRef} className={"btn " + (cur.danger ? "btn-danger" : "btn-primary")}
            onClick={accept}>{cur.confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
